(() => {
  "use strict";

  const existing = window.__jscaCartModal;
  if (existing?.dialog?.isConnected) return;
  existing?.destroy?.();

  const dialog = document.querySelector("[data-cart-modal]");
  if (!dialog || typeof dialog.showModal !== "function") return;

  const panel = dialog.querySelector("[data-cart-modal-panel]");
  const closeButton = dialog.querySelector("[data-cart-modal-close]");
  const content = dialog.querySelector("[data-cart-content]");
  const scrollArea = dialog.querySelector("[data-cart-scroll]");
  const loading = dialog.querySelector("[data-cart-loading]");
  const errorPanel = dialog.querySelector("[data-cart-error]");
  const errorMessage = dialog.querySelector("[data-cart-error-message]");
  const retryButton = dialog.querySelector("[data-cart-retry]");
  const footer = dialog.querySelector("[data-cart-footer]");
  const subtotal = dialog.querySelector("[data-cart-subtotal]");
  const checkout = dialog.querySelector("[data-cart-checkout]");
  const titleCount = dialog.querySelector("[data-cart-title-count]");
  const status = dialog.querySelector("[data-cart-status]");
  const emptyTemplate = dialog.querySelector("[data-cart-empty-template]");

  if (!panel || !closeButton || !content || !scrollArea || !loading || !errorPanel || !errorMessage
    || !retryButton || !footer || !subtotal || !checkout || !titleCount || !status || !emptyTemplate) return;

  const requestTimeout = 10_000;
  const exitDuration = 700;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const cleanup = [];
  const ownCartEvents = new WeakSet();
  const state = {
    cart: null,
    opener: null,
    loadController: null,
    changeController: null,
    loadSequence: 0,
    changeSequence: 0,
    pending: false,
    loading: false,
    closing: false,
    closeTimer: 0,
    refreshTimer: 0,
    pendingMutationRefresh: false,
    invalidated: false,
    openFrames: [],
    loadFocus: null,
    backdropPointerId: null,
    bodyStyles: null,
    restoreFocus: true
  };

  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    cleanup.push(() => target.removeEventListener(type, handler, options));
  };

  const cartCoordinator = (() => {
    const current = window.__jscaCartCoordinator;
    const coordinator = current && typeof current === "object" ? current : {};
    if (!Number.isSafeInteger(coordinator.refreshSequence) || coordinator.refreshSequence < 0) coordinator.refreshSequence = 0;
    if (!Number.isSafeInteger(coordinator.latestRefreshToken) || coordinator.latestRefreshToken < 0) {
      coordinator.latestRefreshToken = coordinator.refreshSequence;
    }
    coordinator.refreshSequence = Math.max(coordinator.refreshSequence, coordinator.latestRefreshToken);
    if (typeof coordinator.reserveRefresh !== "function") {
      coordinator.reserveRefresh = () => {
        coordinator.refreshSequence = coordinator.refreshSequence < Number.MAX_SAFE_INTEGER
          ? coordinator.refreshSequence + 1
          : 1;
        coordinator.latestRefreshToken = coordinator.refreshSequence;
        return coordinator.latestRefreshToken;
      };
    }
    if (typeof coordinator.isLatestRefresh !== "function") {
      coordinator.isLatestRefresh = (token) => token === coordinator.latestRefreshToken;
    }
    window.__jscaCartCoordinator = coordinator;
    return coordinator;
  })();

  const internalRoute = (value, pattern) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol) || url.username || url.password || url.hash) return null;
      return pattern.test(url.pathname) ? `${url.pathname}${url.search}` : null;
    } catch (_) {
      return null;
    }
  };

  const internalPath = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol) || url.username || url.password) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return null;
    }
  };

  const safeImageUrl = (value) => {
    if (!value || typeof value !== "string") return null;
    try {
      const url = new URL(value, window.location.href);
      const hostname = url.hostname.toLowerCase();
      const allowed = url.origin === window.location.origin
        || hostname === "cdn.shopify.com"
        || hostname === "cdn.shopifycdn.net"
        || hostname.endsWith(".myshopify.com");
      return allowed && /^https?:$/.test(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch (_) {
      return null;
    }
  };

  const cartUrl = internalRoute(dialog.dataset.cartUrl, /\/cart\.js$/);
  const changeUrl = internalRoute(dialog.dataset.cartChangeUrl, /\/cart\/change\.js$/);
  if (!cartUrl || !changeUrl) return;

  const fallbackMoney = (() => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: /^[A-Z]{3}$/.test(dialog.dataset.currency || "") ? dialog.dataset.currency : "USD"
      });
    } catch (_) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
    }
  })();

  const formatMoney = (cents) => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return "";
    try {
      return window.JSCAMoney?.format?.(value, dialog.dataset.moneyFormat) || fallbackMoney.format(value / 100);
    } catch (_) {
      return fallbackMoney.format(value / 100);
    }
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
  const safeText = (value, fallback = "") => typeof value === "string" && value.length <= 1_000 ? value : fallback;
  const safeInteger = (value) => Number.isSafeInteger(value) && value >= 0 ? value : null;
  const safePrice = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;

  const normalizeCart = (payload) => {
    if (!isRecord(payload) || !Array.isArray(payload.items) || payload.items.length > 500) return null;
    const itemCount = safeInteger(payload.item_count);
    const totalPrice = safePrice(payload.total_price);
    if (itemCount === null || totalPrice === null) return null;

    const items = payload.items.map((item) => {
      if (!isRecord(item)) return null;
      const quantity = safeInteger(item.quantity);
      const linePrice = safePrice(item.final_line_price ?? item.line_price);
      const key = typeof item.key === "string" && item.key.length > 0 && item.key.length <= 500 ? item.key : null;
      const title = safeText(item.product_title) || safeText(item.title, "Product");
      if (quantity === null || linePrice === null || key === null || !title) return null;
      const featuredImage = isRecord(item.featured_image) ? item.featured_image : {};
      const imageAlt = safeText(featuredImage.alt);
      return {
        key,
        title,
        variant: safeText(item.variant_title),
        quantity,
        linePrice,
        url: internalPath(item.url),
        image: safeImageUrl(featuredImage.url || item.image),
        imageAlt: imageAlt.trim() ? imageAlt : title
      };
    });

    return items.some((item) => item === null) ? null : { itemCount, totalPrice, items, shopifyCart: payload };
  };

  const safeShopifyMessage = (payload) => {
    if (!isRecord(payload)) return "";
    const values = [payload.description, payload.message, payload.error, payload.errors];
    const candidate = values.find((value) => typeof value === "string");
    if (!candidate) return "";
    const message = candidate.replace(/\s+/g, " ").trim();
    return message && message.length <= 240 && !/[<>]/.test(message) ? message : "";
  };

  const requestJson = async (url, options, controller, timeoutCode) => {
    let timedOut = false;
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, requestTimeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const text = await response.text();
      let payload = null;
      try {
        payload = text.trim() ? JSON.parse(text) : null;
      } catch (_) {
        throw Object.assign(new Error("invalid-json"), { response });
      }
      return { response, payload };
    } catch (error) {
      if (timedOut) throw Object.assign(new Error(timeoutCode), { code: timeoutCode });
      throw error;
    } finally {
      window.clearTimeout(timer);
    }
  };

  const triggers = () => Array.from(document.querySelectorAll("[data-cart-modal-open]"));
  const syncTriggers = () => {
    const expanded = dialog.open && !state.closing;
    triggers().forEach((trigger) => trigger.setAttribute("aria-expanded", String(expanded)));
  };

  const lockBody = () => {
    if (state.bodyStyles) return;
    const body = document.body;
    const computedPadding = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    state.bodyStyles = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    body.style.overflow = "hidden";
    body.style.paddingRight = `${computedPadding + scrollbarWidth}px`;
    body.classList.add("jsca-cart-modal-lock");
  };

  const unlockBody = () => {
    if (!state.bodyStyles) return;
    document.body.classList.remove("jsca-cart-modal-lock");
    document.body.style.overflow = state.bodyStyles.overflow;
    document.body.style.paddingRight = state.bodyStyles.paddingRight;
    state.bodyStyles = null;
  };

  const setControlsDisabled = () => {
    const disabled = state.loading || state.pending;
    content.querySelectorAll("[data-cart-action]").forEach((button) => {
      button.setAttribute("aria-disabled", String(disabled));
    });
    checkout.disabled = disabled || state.cart?.itemCount === 0;
    dialog.setAttribute("aria-busy", String(disabled));
  };

  const rememberFocus = (active = document.activeElement) => {
    const button = active?.closest?.("[data-cart-action]");
    const item = button?.closest?.("[data-cart-line-key]");
    if (button && item) {
      return {
        kind: "line",
        key: item.dataset.cartLineKey,
        action: button.dataset.cartAction,
        index: Array.from(content.querySelectorAll("[data-cart-line-key]")).indexOf(item)
      };
    }
    return active === retryButton ? { kind: "fallback" } : null;
  };

  const restoreRememberedFocus = (memory) => {
    if (!memory || !dialog.open || document.querySelector("[data-quick-view][open]")) return;
    let target = null;
    if (memory.kind === "line") {
      const lines = Array.from(content.querySelectorAll("[data-cart-line-key]"));
      const line = lines.find((candidate) => candidate.dataset.cartLineKey === memory.key)
        || lines[Math.max(0, Math.min(memory.index, lines.length - 1))];
      target = Array.from(line?.querySelectorAll("[data-cart-action]") || [])
        .find((candidate) => candidate.dataset.cartAction === memory.action && candidate.getAttribute("aria-disabled") !== "true")
        || Array.from(line?.querySelectorAll("[data-cart-action]") || [])
          .find((candidate) => candidate.getAttribute("aria-disabled") !== "true");
      if (!line) {
        target = content.querySelector(".jsca-cart-modal__top-picks a") || dialog.querySelector("#JscaCartModalTitle");
        if (target?.matches("#JscaCartModalTitle")) target.tabIndex = -1;
      }
    }
    (target || closeButton).focus({ preventScroll: true });
  };

  const buttonFor = (action, label, text, key, quantity) => {
    const button = createElement("button", action === "remove" ? "jsca-cart-modal__remove" : "", text);
    button.type = "button";
    button.dataset.cartAction = action;
    button.dataset.cartKey = key;
    button.dataset.cartQuantity = String(quantity);
    button.setAttribute("aria-label", label);
    return button;
  };

  const itemElement = (item) => {
    const row = createElement("li", "jsca-cart-modal__item");
    row.dataset.cartLineKey = item.key;

    const mediaTag = item.url ? "a" : "div";
    const media = createElement(mediaTag, "jsca-cart-modal__item-media");
    if (item.url) {
      media.href = item.url;
      media.setAttribute("aria-label", `View ${item.title}`);
    }
    if (item.image) {
      const image = createElement("img", "jsca-cart-modal__item-image");
      image.src = item.image;
      image.alt = item.imageAlt;
      image.loading = "lazy";
      image.width = 176;
      image.height = 224;
      media.append(image);
    } else {
      const fallback = createElement("span", "jsca-cart-modal__item-image-fallback", "Product image unavailable");
      fallback.setAttribute("role", "img");
      fallback.setAttribute("aria-label", `Product image unavailable for ${item.title}`);
      media.append(fallback);
    }

    const details = createElement("div", "jsca-cart-modal__item-details");
    const titleTag = item.url ? "a" : "p";
    const title = createElement(titleTag, "jsca-cart-modal__item-title", item.title);
    if (item.url) title.href = item.url;
    details.append(title);
    if (item.variant && item.variant.toLowerCase() !== "default title") {
      details.append(createElement("p", "jsca-cart-modal__item-variant", item.variant));
    }
    details.append(createElement("p", "jsca-cart-modal__item-price", formatMoney(item.linePrice)));

    const controls = createElement("div", "jsca-cart-modal__item-controls");
    controls.setAttribute("role", "group");
    controls.setAttribute("aria-label", `Quantity for ${item.title}`);
    const decreaseQuantity = Math.max(0, item.quantity - 1);
    controls.append(
      buttonFor("decrease", `Decrease quantity of ${item.title}`, "−", item.key, decreaseQuantity),
      createElement("span", "jsca-cart-modal__quantity", String(item.quantity)),
      buttonFor("increase", `Increase quantity of ${item.title}`, "+", item.key, item.quantity + 1),
      buttonFor("remove", `Remove ${item.title} from bag`, "Remove", item.key, 0)
    );
    details.append(controls);
    row.append(media, details);
    return row;
  };

  const renderCart = (cart, focusMemory = rememberFocus(), keepError = false) => {
    const scrollTop = scrollArea.scrollTop;
    state.cart = cart;
    if (!keepError) errorPanel.hidden = true;
    content.hidden = false;
    titleCount.textContent = `(${cart.itemCount})`;
    subtotal.textContent = formatMoney(cart.totalPrice);
    footer.hidden = cart.items.length === 0;

    if (cart.items.length === 0) {
      content.replaceChildren(emptyTemplate.content.cloneNode(true));
    } else {
      const list = createElement("ul", "jsca-cart-modal__items");
      list.setAttribute("role", "list");
      cart.items.forEach((item) => list.append(itemElement(item)));
      content.replaceChildren(list);
    }

    setControlsDisabled();
    window.requestAnimationFrame(() => {
      if (!dialog.open) return;
      scrollArea.scrollTop = scrollTop;
      restoreRememberedFocus(focusMemory);
    });
  };

  const showError = (message) => {
    errorMessage.textContent = message;
    errorPanel.hidden = false;
    if (!state.cart) content.hidden = true;
    setControlsDisabled();
  };

  const setLoading = (value) => {
    state.loading = value;
    loading.hidden = !value;
    setControlsDisabled();
  };

  const dispatchCart = (cart, refreshToken, mutation = false) => {
    const event = new CustomEvent("jsca:cart:updated", {
      detail: { cart: cart.shopifyCart, refreshToken, mutation }
    });
    ownCartEvents.add(event);
    document.dispatchEvent(event);
  };

  const dispatchMutationInvalidation = () => {
    const event = new CustomEvent("jsca:cart:updated", {
      detail: { cart: null, mutation: true }
    });
    ownCartEvents.add(event);
    document.dispatchEvent(event);
  };

  const queueRefresh = (mutation = false) => {
    if (!dialog.open || state.closing) return;
    state.pendingMutationRefresh = state.pendingMutationRefresh || mutation === true;
    if (state.refreshTimer) return;
    state.refreshTimer = window.setTimeout(() => {
      state.refreshTimer = 0;
      void loadCart();
    }, 0);
  };

  const loadCart = async ({ announce = "", dispatch = true, mutation = false, preserveError = false, focusMemory = null } = {}) => {
    if (!dialog.open || state.closing) return;
    state.pendingMutationRefresh = state.pendingMutationRefresh || mutation === true;
    const effectiveMutation = state.pendingMutationRefresh;
    const rememberedFocus = focusMemory || state.loadFocus || rememberFocus();
    state.loadFocus = rememberedFocus;
    state.loadController?.abort();
    const controller = new AbortController();
    const sequence = ++state.loadSequence;
    const refreshToken = cartCoordinator.reserveRefresh();
    state.loadController = controller;
    setLoading(true);
    status.textContent = "Loading your bag.";
    if (!preserveError) errorPanel.hidden = true;

    try {
      const { response, payload } = await requestJson(cartUrl, {
        cache: "no-store",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" }
      }, controller, "cart-timeout");
      if (!response.ok) throw Object.assign(new Error("cart-failed"), { payload });
      const cart = normalizeCart(payload);
      if (!cart) throw new Error("invalid-cart");
      if (sequence !== state.loadSequence || !dialog.open || state.closing) return;
      if (!cartCoordinator.isLatestRefresh(refreshToken)) {
        queueRefresh(effectiveMutation);
        return;
      }
      renderCart(cart, rememberedFocus, preserveError);
      state.loadFocus = null;
      if (preserveError) {
        errorMessage.textContent = "The update could not be confirmed. Your current bag was refreshed; review it before trying again.";
        errorPanel.hidden = false;
      }
      status.textContent = announce || "Bag loaded.";
      if (dispatch) {
        dispatchCart(cart, refreshToken, effectiveMutation);
        if (effectiveMutation) state.pendingMutationRefresh = false;
      }
    } catch (error) {
      if (sequence !== state.loadSequence || !dialog.open || state.closing) return;
      if (controller.signal.aborted && error.code !== "cart-timeout") return;
      showError(error.code === "cart-timeout"
        ? "Your bag took too long to load. Retry or view your bag."
        : "We could not load your bag. Retry or view your bag.");
    } finally {
      if (state.loadController === controller) state.loadController = null;
      if (sequence === state.loadSequence) setLoading(false);
    }
  };

  const changeLine = async (button) => {
    if (state.loading || state.pending || state.closing || button.getAttribute("aria-disabled") === "true") return;
    const key = button.dataset.cartKey;
    const quantity = Number(button.dataset.cartQuantity);
    if (!key || !Number.isSafeInteger(quantity) || quantity < 0) return;

    const focusMemory = rememberFocus(button);
    const controller = new AbortController();
    const sequence = ++state.changeSequence;
    const refreshToken = cartCoordinator.reserveRefresh();
    state.changeController?.abort();
    state.changeController = controller;
    state.loadController?.abort();
    state.loadSequence += 1;
    if (state.refreshTimer) {
      window.clearTimeout(state.refreshTimer);
      state.refreshTimer = 0;
      state.invalidated = true;
    }
    state.pending = true;
    errorPanel.hidden = true;
    setControlsDisabled();
    status.textContent = quantity === 0 ? "Removing item from bag." : "Updating bag quantity.";

    try {
      const { response, payload } = await requestJson(changeUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ id: key, quantity })
      }, controller, "change-timeout");
      if (!response.ok) {
        throw Object.assign(new Error("change-failed"), {
          userMessage: safeShopifyMessage(payload) || "This quantity is unavailable. Retry or view your bag."
        });
      }
      const cart = normalizeCart(payload);
      if (!cart) throw new Error("invalid-cart");
      if (sequence !== state.changeSequence || !dialog.open || state.closing) return;

      if (cartCoordinator.isLatestRefresh(refreshToken) && !state.invalidated) {
        renderCart(cart, focusMemory);
        status.textContent = quantity === 0 ? "Item removed from bag." : "Bag quantity updated.";
        dispatchCart(cart, refreshToken, true);
        state.pendingMutationRefresh = false;
      } else {
        await loadCart({ announce: "Bag updated.", dispatch: true, mutation: true, focusMemory });
      }
    } catch (error) {
      if (sequence !== state.changeSequence || !dialog.open || state.closing) return;
      if (controller.signal.aborted && error.code !== "change-timeout") return;
      const ambiguous = error.code === "change-timeout" || !error.userMessage;
      if (ambiguous || state.invalidated) {
        showError(error.code === "change-timeout"
          ? "The bag update timed out. We are checking your bag before you try again."
          : "We could not confirm the bag update. We are checking your bag before you try again.");
        status.textContent = "Checking bag after an uncertain update.";
        await loadCart({ announce: "Bag refreshed after an uncertain update.", dispatch: true, mutation: true, preserveError: true, focusMemory });
      } else {
        showError(error.userMessage);
        status.textContent = "Bag update failed.";
      }
    } finally {
      if (sequence === state.changeSequence) {
        state.pending = false;
        state.invalidated = false;
        setControlsDisabled();
        if (dialog.open && !document.querySelector("[data-quick-view][open]")) {
          window.requestAnimationFrame(() => restoreRememberedFocus(focusMemory));
        }
      }
      if (state.changeController === controller) state.changeController = null;
    }
  };

  const abortRequests = () => {
    state.loadSequence += 1;
    state.changeSequence += 1;
    state.loadController?.abort();
    state.changeController?.abort();
    state.loadController = null;
    state.changeController = null;
    state.loading = false;
    state.pending = false;
    state.invalidated = false;
    state.pendingMutationRefresh = false;
    state.loadFocus = null;
    loading.hidden = true;
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = 0;
    setControlsDisabled();
  };

  const cancelOpenFrames = () => {
    state.openFrames.forEach((frame) => window.cancelAnimationFrame(frame));
    state.openFrames = [];
  };

  const finishClose = () => {
    window.clearTimeout(state.closeTimer);
    state.closeTimer = 0;
    if (dialog.open) dialog.close();
  };

  const closeCart = ({ immediate = false, restoreFocus = true } = {}) => {
    if (!dialog.open || state.closing) return;
    state.closing = true;
    state.restoreFocus = restoreFocus;
    panel.inert = true;
    cancelOpenFrames();
    dialog.classList.remove("is-opening");
    dialog.classList.add("is-closing");
    syncTriggers();
    if (state.pending || state.pendingMutationRefresh) dispatchMutationInvalidation();
    abortRequests();
    if (immediate || reducedMotion.matches) finishClose();
    else state.closeTimer = window.setTimeout(finishClose, exitDuration);
  };

  const openCart = (trigger) => {
    if (state.closing && dialog.open) {
      window.clearTimeout(state.closeTimer);
      state.closeTimer = 0;
      state.closing = false;
      panel.inert = false;
      dialog.classList.remove("is-closing");
      state.opener = trigger;
      syncTriggers();
      void loadCart();
      closeButton.focus({ preventScroll: true });
      return true;
    }
    if (dialog.open) {
      state.opener = trigger;
      syncTriggers();
      closeButton.focus({ preventScroll: true });
      return true;
    }

    state.opener = trigger;
    state.restoreFocus = true;
    state.closing = false;
    panel.inert = false;
    errorPanel.hidden = true;
    content.hidden = false;
    dialog.classList.remove("is-closing");
    dialog.classList.add("is-opening");
    try {
      dialog.showModal();
    } catch (_) {
      dialog.classList.remove("is-opening");
      state.opener = null;
      return false;
    }
    lockBody();
    syncTriggers();
    closeButton.focus({ preventScroll: true });
    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        state.openFrames = [];
        if (dialog.open && !state.closing) dialog.classList.remove("is-opening");
      });
      state.openFrames.push(secondFrame);
    });
    state.openFrames.push(firstFrame);
    void loadCart();
    return true;
  };

  const resetForPageHide = () => {
    state.restoreFocus = false;
    window.clearTimeout(state.closeTimer);
    state.closeTimer = 0;
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = 0;
    cancelOpenFrames();
    abortRequests();
    state.closing = false;
    state.invalidated = false;
    panel.inert = false;
    dialog.classList.remove("is-opening", "is-closing");
    if (dialog.open) dialog.close();
    unlockBody();
    syncTriggers();
    state.opener = null;
    state.restoreFocus = true;
  };

  const onDocumentClick = (event) => {
    const trigger = event.target.closest?.("[data-cart-modal-open]");
    if (!trigger || event.defaultPrevented || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (openCart(trigger)) event.preventDefault();
  };

  const onCartUpdated = (event) => {
    if (ownCartEvents.has(event)) return;
    if (!dialog.open || state.closing) return;
    if (state.pending) state.invalidated = true;
    else queueRefresh();
  };

  listen(document, "click", onDocumentClick);
  listen(document, "jsca:cart:updated", onCartUpdated);
  listen(dialog, "pointerdown", (event) => {
    state.backdropPointerId = event.isPrimary && event.button === 0 && event.target === dialog ? event.pointerId : null;
  });
  listen(dialog, "pointerup", (event) => {
    const shouldClose = state.backdropPointerId === event.pointerId && event.target === dialog;
    state.backdropPointerId = null;
    if (shouldClose) closeCart();
  });
  listen(dialog, "pointercancel", (event) => {
    if (state.backdropPointerId === event.pointerId) state.backdropPointerId = null;
  });
  listen(dialog, "click", (event) => {
    if (event.target.closest?.("[data-cart-modal-close]")) {
      closeCart();
      return;
    }
    if (event.target.closest?.("[data-cart-retry]")) {
      void loadCart();
      return;
    }
    const action = event.target.closest?.("[data-cart-action]");
    if (action && dialog.contains(action)) void changeLine(action);
  });
  listen(dialog, "cancel", (event) => {
    event.preventDefault();
    closeCart();
  });
  listen(dialog, "close", () => {
    window.clearTimeout(state.closeTimer);
    state.closeTimer = 0;
    cancelOpenFrames();
    dialog.classList.remove("is-opening", "is-closing");
    state.closing = false;
    panel.inert = false;
    abortRequests();
    unlockBody();
    syncTriggers();
    const opener = state.opener;
    state.opener = null;
    if (state.restoreFocus && opener?.isConnected) {
      window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
    }
    state.restoreFocus = true;
  });
  listen(document, "shopify:section:load", () => {
    syncTriggers();
    if (dialog.open && !state.opener?.isConnected) state.opener = triggers()[0] || null;
  });
  listen(window, "pagehide", resetForPageHide);
  listen(window, "pageshow", syncTriggers);

  const destroy = () => {
    resetForPageHide();
    cleanup.splice(0).reverse().forEach((dispose) => dispose());
    if (window.__jscaCartModal?.dialog === dialog) delete window.__jscaCartModal;
  };

  syncTriggers();
  window.__jscaCartModal = { dialog, close: closeCart, open: openCart, destroy };
})();
